import React, { useEffect, useState } from "react";
import { Routes, Route, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import api, { fmtCLP, resolveImg } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { LayoutDashboard, PlusCircle, MailQuestion, MessageSquare, Briefcase, Send, ChevronRight, Trash2, Upload, Image as ImageIcon, Eye, Heart, Mail, TrendingUp, Flame, X, Plus, Clock, Check, FileLock2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import StatusBadge from "../components/StatusBadge";
import BusinessDocuments from "../components/BusinessDocuments";
import ProcessTimeline from "../components/ProcessTimeline";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const NAV = [
  { to: "/seller", icon: <LayoutDashboard className="w-4 h-4" />, label: "Panel", end: true },
  { to: "/seller/new", icon: <PlusCircle className="w-4 h-4" />, label: "Publicar negocio" },
  { to: "/seller/businesses", icon: <Briefcase className="w-4 h-4" />, label: "Mis negocios", section: "businesses" },
  { to: "/seller/requests", icon: <MailQuestion className="w-4 h-4" />, label: "Solicitudes", section: "requests" },
];

export default function SellerPanel() {
  return (
    <DashboardLayout title="Vendedor" subtitle="Panel del vendedor" items={NAV} testId="seller-panel">
      <Routes>
        <Route index element={<Overview />} />
        <Route path="businesses" element={<MyBusinesses />} />
        <Route path="new" element={<NewBusiness />} />
        <Route path="edit/:id" element={<EditBusiness />} />
        <Route path="requests" element={<MyRequests />} />
        <Route path="documents" element={<DocAccessRequests />} />
      </Routes>
    </DashboardLayout>
  );
}

function Overview() {
  const [biz, setBiz] = useState([]);
  const [reqs, setReqs] = useState([]);
  useEffect(() => {
    api.get("/seller/businesses").then((r) => setBiz(r.data));
    api.get("/requests/mine").then((r) => setReqs(r.data));
  }, []);
  const totalViews = biz.reduce((s, b) => s + (b.views_count || 0), 0);
  const totalFavs = biz.reduce((s, b) => s + (b.favorites_count || 0), 0);
  const stats = [
    { label: "Visualizaciones totales", value: totalViews, icon: <Eye className="w-4 h-4" />, accent: "slate" },
    { label: "Inversionistas interesados", value: totalFavs, icon: <Heart className="w-4 h-4" />, accent: "rose" },
    { label: "Solicitudes recibidas", value: reqs.length, icon: <Mail className="w-4 h-4" />, accent: "emerald" },
    { label: "Negocios aprobados", value: biz.filter((b) => b.status === "approved").length, icon: <TrendingUp className="w-4 h-4" />, accent: "amber" },
  ];
  return (
    <div className="space-y-6" data-testid="seller-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => (
          <SummaryCard key={s.label} icon={s.icon} label={s.label} value={s.value} accent={s.accent} />
        ))}
      </div>
      <div className="bg-slate-900 text-white rounded-lg p-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="font-display text-xl font-bold">¿Listo para publicar tu próximo negocio?</h3>
          <p className="text-sm text-slate-300 mt-1">Te guiamos paso a paso. Puedes guardar como borrador.</p>
        </div>
        <Button asChild className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="overview-new-biz">
          <Link to="/seller/new"><PlusCircle className="w-4 h-4 mr-2" /> Publicar negocio</Link>
        </Button>
      </div>
    </div>
  );
}

function MyBusinesses() {
  const [list, setList] = useState([]);
  const load = () => api.get("/seller/businesses").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (b) => {
    try {
      await api.post(`/seller/businesses/${b.id}/submit`);
      toast.success("Enviado a revisión");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar");
    }
  };
  const del = async (b) => {
    if (!window.confirm("¿Eliminar este negocio?")) return;
    await api.delete(`/seller/businesses/${b.id}`);
    load();
  };

  const totals = list.reduce(
    (acc, b) => ({
      views: acc.views + (b.views_count || 0),
      favs: acc.favs + (b.favorites_count || 0),
      reqs: acc.reqs + (b.requests_count || 0),
    }),
    { views: 0, favs: 0, reqs: 0 }
  );
  const topId = list.length > 0 ? [...list].sort((a, b) => (b.views_count || 0) - (a.views_count || 0))[0]?.id : null;

  return (
    <div className="space-y-5" data-testid="seller-businesses">
      {list.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCard icon={<Eye className="w-4 h-4" />} label="Visualizaciones" value={totals.views} accent="slate" />
          <SummaryCard icon={<Heart className="w-4 h-4" />} label="Interesados" value={totals.favs} accent="rose" />
          <SummaryCard icon={<Mail className="w-4 h-4" />} label="Solicitudes" value={totals.reqs} accent="emerald" />
          <SummaryCard icon={<TrendingUp className="w-4 h-4" />} label="Publicaciones activas" value={list.filter(b => b.status === "approved").length} accent="amber" />
        </div>
      )}

      <Button asChild className="btn-primary" data-testid="new-biz-btn"><Link to="/seller/new"><PlusCircle className="w-4 h-4 mr-2" /> Nuevo negocio</Link></Button>
      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Negocio</TableHead>
            <TableHead>Precio</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-center">Interés generado</TableHead>
            <TableHead>Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-10">
                Aún no has publicado negocios. <Link to="/seller/new" className="font-semibold text-slate-900 underline">Crear el primero</Link>
              </TableCell></TableRow>
            )}
            {list.map((b) => (
              <TableRow key={b.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{b.title}</span>
                    {b.is_featured && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                        <Flame className="w-3 h-3" /> Destacado
                      </span>
                    )}
                    {topId === b.id && (b.views_count || 0) > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                        <TrendingUp className="w-3 h-3" /> El más visto
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{b.category_name}</div>
                </TableCell>
                <TableCell>{fmtCLP(b.sale_price)}</TableCell>
                <TableCell><StatusBadge status={b.status} /></TableCell>
                <TableCell>
                  <div className="flex items-center justify-center gap-3 text-xs">
                    <MetricInline icon={<Eye className="w-3.5 h-3.5" />} value={b.views_count || 0} label="vistas" tone="slate" />
                    <MetricInline icon={<Heart className="w-3.5 h-3.5" />} value={b.favorites_count || 0} label="favoritos" tone="rose" />
                    <MetricInline icon={<Mail className="w-3.5 h-3.5" />} value={b.requests_count || 0} label="solicitudes" tone="emerald" />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button asChild size="sm" variant="outline" data-testid={`edit-biz-${b.id}`}><Link to={`/seller/edit/${b.id}`}>Editar</Link></Button>
                    {b.status === "draft" && (<Button size="sm" onClick={() => submit(b)} data-testid={`submit-biz-${b.id}`}>Enviar a revisión</Button>)}
                    {b.status === "rejected" && (<Button size="sm" onClick={() => submit(b)}>Reenviar</Button>)}
                    <Button size="icon" variant="ghost" onClick={() => del(b)} data-testid={`delete-biz-${b.id}`} title="Eliminar"><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value, accent }) {
  const tone = {
    slate:   { wrap: "bg-white border-zinc-200",     ico: "bg-slate-100 text-slate-700" },
    rose:    { wrap: "bg-white border-zinc-200",     ico: "bg-rose-100 text-rose-700" },
    emerald: { wrap: "bg-white border-zinc-200",     ico: "bg-emerald-100 text-emerald-700" },
    amber:   { wrap: "bg-white border-zinc-200",     ico: "bg-amber-100 text-amber-700" },
  }[accent] || { wrap: "bg-white border-zinc-200", ico: "bg-slate-100 text-slate-700" };
  return (
    <div className={`border ${tone.wrap} rounded-lg p-4`}>
      <div className="flex items-center justify-between">
        <span className={`w-7 h-7 rounded-md flex items-center justify-center ${tone.ico}`}>{icon}</span>
        <span className="font-display text-2xl font-extrabold">{value}</span>
      </div>
      <p className="mt-2 text-xs text-slate-500 uppercase tracking-widest">{label}</p>
    </div>
  );
}

function MetricInline({ icon, value, label, tone }) {
  const cls = tone === "rose" ? "text-rose-600" : tone === "emerald" ? "text-emerald-700" : "text-slate-600";
  return (
    <span title={`${value} ${label}`} className={`inline-flex items-center gap-1 ${cls} font-semibold`}>
      {icon} {value}
    </span>
  );
}

const EMPTY = {
  title: "", description: "", sale_reason: "", category_id: "", subcategory_id: "",
  region_id: "", comuna_id: "", years_operating: "", employees_count: "",
  operating_hours: "", monthly_revenue: "", monthly_costs: "", monthly_profit: "",
  sale_price: "", rent_amount: "", inventory_included: "", assets_included: "",
  permits: "", social_links: "", includes_training: false, includes_suppliers: false,
  includes_clients: false, confidentiality_level: "medium", show_profit_publicly: true,
};

function BusinessForm({ initial = EMPTY, onSave, onUploadImage, images = [], onDeleteImage, businessId = null, createMode = false }) {
  const [form, setForm] = useState(initial);
  const [regions, setRegions] = useState([]);
  const [comunas, setComunas] = useState([]);
  const [cats, setCats] = useState([]);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/regions").then((r) => setRegions(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, []);
  useEffect(() => {
    if (form.region_id) api.get(`/regions/${form.region_id}/comunas`).then((r) => setComunas(r.data));
  }, [form.region_id]);

  const upd = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ["years_operating", "employees_count", "monthly_revenue", "monthly_costs", "monthly_profit", "sale_price", "rent_amount"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
        else payload[k] = parseFloat(payload[k]);
      });
      ["sale_reason", "operating_hours", "inventory_included", "assets_included", "permits", "social_links"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-6 max-w-4xl" data-testid="business-form">
      <div className="flex items-center gap-2 text-xs">
        {["Información básica", "Detalles operativos", "Financiero", "Imágenes y documentos"].map((label, i) => (
          <React.Fragment key={i}>
            <button onClick={() => setStep(i + 1)} className={`px-3 py-1.5 rounded-full font-semibold ${step === i + 1 ? "bg-slate-900 text-white" : "bg-zinc-100 text-slate-500"}`} data-testid={`step-${i + 1}`}>{i + 1}. {label}</button>
            {i < 3 && <ChevronRight className="w-3 h-3 text-slate-400" />}
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <div><Label>Nombre comercial *</Label><Input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="biz-title" /></div>
          <div><Label>Descripción general *</Label><Textarea rows={5} value={form.description} onChange={(e) => upd("description", e.target.value)} data-testid="biz-description" /></div>
          <div><Label>Motivo de venta</Label><Textarea rows={2} value={form.sale_reason} onChange={(e) => upd("sale_reason", e.target.value)} /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Categoría *</Label>
              <Select value={form.category_id} onValueChange={(v) => upd("category_id", v)}>
                <SelectTrigger data-testid="biz-category"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Región *</Label>
              <Select value={form.region_id} onValueChange={(v) => upd("region_id", v)}>
                <SelectTrigger data-testid="biz-region"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {comunas.length > 0 && (
            <div>
              <Label>Comuna *</Label>
              <Select value={form.comuna_id} onValueChange={(v) => upd("comuna_id", v)}>
                <SelectTrigger data-testid="biz-comuna"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{comunas.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Años operando</Label><Input type="number" value={form.years_operating} onChange={(e) => upd("years_operating", e.target.value)} /></div>
            <div><Label>N° de trabajadores</Label><Input type="number" value={form.employees_count} onChange={(e) => upd("employees_count", e.target.value)} /></div>
          </div>

          <ScheduleEditor value={form.operating_hours} onChange={(v) => upd("operating_hours", v)} />

          <ChipSelect
            label="Inventario incluido"
            help="Marca lo que se transfiere al nuevo dueño. Puedes agregar más si no aparece en la lista."
            options={["Mercadería en bodega", "Stock visible en local", "Materias primas", "Productos terminados", "Repuestos", "Insumos generales"]}
            value={form.inventory_included}
            onChange={(v) => upd("inventory_included", v)}
            testId="inventory"
          />

          <ChipSelect
            label="Activos incluidos"
            help="Bienes físicos y equipamiento que recibe el comprador."
            options={["Mobiliario", "Maquinaria", "Equipos de cómputo", "Sistema POS / caja", "Refrigeradores", "Hornos / cocinas", "Aire acondicionado", "Letreros y señalética", "Vehículos", "Cámaras de seguridad", "Equipamiento deportivo", "Herramientas"]}
            value={form.assets_included}
            onChange={(v) => upd("assets_included", v)}
            testId="assets"
          />

          <ChipSelect
            label="Patentes o permisos vigentes"
            help="Documentos legales y municipales que el negocio tiene al día."
            options={["Patente comercial municipal", "Patente de alcohol", "Resolución sanitaria", "Permiso de bomberos", "Permiso de obras", "Iniciación de actividades SII", "Permiso SERNATUR", "Permiso de funcionamiento gimnasio", "Registro sanitario ISP"]}
            value={form.permits}
            onChange={(v) => upd("permits", v)}
            testId="permits"
          />

          <div>
            <Label>¿Qué transfieres al nuevo dueño?</Label>
            <p className="text-xs text-slate-500 mt-1">Marca lo que se incluye en la venta. Esto le da más confianza al comprador.</p>
            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              <IncludeCard
                title="Capacitación"
                desc="El vendedor entrena al comprador en procesos, atención al cliente, sistemas y operación diaria por un período acordado."
                checked={form.includes_training}
                onChange={(v) => upd("includes_training", v)}
              />
              <IncludeCard
                title="Cartera de proveedores"
                desc="Se transfiere la lista de proveedores con sus contactos, condiciones de pago, precios y créditos vigentes."
                checked={form.includes_suppliers}
                onChange={(v) => upd("includes_suppliers", v)}
              />
              <IncludeCard
                title="Cartera de clientes"
                desc="Se entrega la base de datos / CRM de clientes activos, contactos, historial de compras y programas de fidelización."
                checked={form.includes_clients}
                onChange={(v) => upd("includes_clients", v)}
              />
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Ventas mensuales</Label><MoneyInput value={form.monthly_revenue} onChange={(v) => upd("monthly_revenue", v)} placeholder="$0" /></div>
            <div><Label>Costos mensuales</Label><MoneyInput value={form.monthly_costs} onChange={(v) => upd("monthly_costs", v)} placeholder="$0" /></div>
            <div><Label>Utilidad mensual</Label><MoneyInput value={form.monthly_profit} onChange={(v) => upd("monthly_profit", v)} placeholder="$0" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Precio de venta *</Label><MoneyInput value={form.sale_price} onChange={(v) => upd("sale_price", v)} data-testid="biz-price" placeholder="$0" /></div>
            <div><Label>Arriendo del local</Label><MoneyInput value={form.rent_amount} onChange={(v) => upd("rent_amount", v)} placeholder="$0" /></div>
          </div>
          <label className="flex items-center justify-between border border-zinc-200 rounded-lg p-3"><span className="text-sm">Mostrar finanzas públicamente</span><Switch checked={form.show_profit_publicly} onCheckedChange={(v) => upd("show_profit_publicly", v)} /></label>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-6" data-testid="step4-media">
          {createMode && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 flex items-start gap-3" data-testid="create-mode-hint">
              <ImageIcon className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold mb-1">Guarda primero como borrador</p>
                <p className="text-xs leading-relaxed">
                  Para subir <strong>imágenes</strong> y <strong>documentos privados</strong> (balance, contratos, patentes, etc.), primero completa los pasos anteriores y presiona <em>Guardar</em>.
                  Te llevaremos a la pantalla de edición donde podrás subir hasta 8MB por foto y 15MB por documento.
                </p>
                <Button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="btn-investment mt-3"
                  data-testid="save-and-upload-btn"
                >
                  {saving ? "Guardando..." : "Guardar borrador y subir archivos"}
                </Button>
              </div>
            </div>
          )}

          {onUploadImage && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-slate-600" />
                <Label className="m-0">Imágenes del negocio</Label>
              </div>
              <p className="text-xs text-slate-500">Sube fotos del local, productos y equipamiento. JPG/PNG, máx 8MB. La primera imagen es la portada.</p>
              <label className="border-2 border-dashed border-zinc-200 rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer hover:border-slate-900 transition" data-testid="upload-area">
                <Upload className="w-8 h-8 text-slate-400" />
                <span className="mt-2 text-sm font-semibold">Subir imagen</span>
                <span className="text-xs text-slate-500">JPG/PNG, máx 8MB</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onUploadImage(e.target.files[0])} data-testid="upload-input" />
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {images.map((img) => (
                  <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden border border-zinc-200 group">
                    <img src={resolveImg(img.storage_path)} alt="" className="w-full h-full object-cover" />
                    {img.is_primary && <Badge className="absolute top-2 left-2 bg-amber-500 text-slate-900">Principal</Badge>}
                    {onDeleteImage && (
                      <button onClick={() => onDeleteImage(img.id)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-rose-600 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center" data-testid={`del-img-${img.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                {images.length === 0 && (
                  <div className="aspect-square rounded-lg border-2 border-dashed border-zinc-200 flex items-center justify-center text-slate-400">
                    <ImageIcon className="w-8 h-8" />
                  </div>
                )}
              </div>
            </div>
          )}

          {businessId && (
            <div className="pt-2 border-t border-zinc-100">
              <BusinessDocuments businessId={businessId} />
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between pt-4 border-t border-zinc-100">
        <Button variant="outline" disabled={step === 1} onClick={() => setStep(step - 1)}>Anterior</Button>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} className="btn-primary" data-testid="save-business-btn">{saving ? "Guardando..." : "Guardar"}</Button>
          {step < 4 && <Button onClick={() => setStep(step + 1)} className="btn-investment">Siguiente</Button>}
        </div>
      </div>
    </div>
  );
}

export function NewBusiness() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminScope = location.pathname.startsWith("/admin");
  const editBase = isAdminScope ? "/admin/edit" : "/seller/edit";
  const save = async (payload) => {
    if (!payload.title || !payload.description || !payload.sale_price) {
      toast.error("Completa título, descripción y precio");
      return;
    }
    const r = await api.post("/seller/businesses", payload);
    toast.success("Negocio creado como borrador");
    navigate(`${editBase}/${r.data.id}`);
  };
  return (
    <div className="max-w-4xl">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <p className="text-xs text-amber-900 leading-relaxed">
          <strong>Responsabilidad del vendedor:</strong> al publicar declaras que la información es veraz y completa.
          DerechoLlave actúa como intermediario y no responde por declaraciones incorrectas, ocultamientos o
          cualquier consecuencia derivada de la venta. Ver <Link to="/terminos" target="_blank" className="underline font-semibold">términos</Link>.
        </p>
      </div>
      <p className="text-sm text-slate-500 mb-4">Completa los pasos. Puedes guardar como borrador y volver más tarde para subir imágenes y documentos privados.</p>
      <BusinessForm onSave={save} createMode={true} />
    </div>
  );
}

export function EditBusiness() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminScope = location.pathname.startsWith("/admin");
  const listBase = isAdminScope ? "/admin/businesses" : "/seller/businesses";
  const [biz, setBiz] = useState(null);
  const load = () => api.get("/seller/businesses").then((r) => {
    const b = r.data.find((x) => x.id === id);
    setBiz(b);
  });
  useEffect(() => { load(); }, [id]);
  if (!biz) return <p>Cargando...</p>;

  const initial = {
    ...EMPTY,
    ...biz,
    sale_price: biz.sale_price ?? "",
  };

  const save = async (payload) => {
    await api.put(`/seller/businesses/${id}`, payload);
    toast.success("Negocio actualizado");
    load();
  };
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    await api.post(`/upload/business-image/${id}?is_primary=${biz.images.length === 0}`, fd);
    toast.success("Imagen subida");
    load();
  };
  const delImg = async (imgId) => {
    await api.delete(`/upload/business-image/${imgId}`);
    load();
  };
  const submit = async () => {
    try {
      await api.post(`/seller/businesses/${id}/submit`);
      toast.success("Enviado a revisión del admin");
      navigate(listBase);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar");
    }
  };

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <div><h2 className="font-display text-xl font-bold">{biz.title}</h2><StatusBadge status={biz.status} /></div>
        {(biz.status === "draft" || biz.status === "rejected") && (
          <Button onClick={submit} className="btn-investment" data-testid="submit-for-review"><Send className="w-4 h-4 mr-2" /> Enviar a revisión</Button>
        )}
      </div>
      <BusinessForm initial={initial} onSave={save} onUploadImage={upload} images={biz.images} onDeleteImage={delImg} businessId={id} />
    </div>
  );
}

function DocAccessRequests() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [notesById, setNotesById] = useState({});
  const load = () => api.get("/seller/document-access").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const decide = async (id, status) => {
    try {
      await api.patch(`/seller/document-access/${id}`, { status, decision_notes: notesById[id] || null });
      toast.success(status === "approved" ? "Acceso aprobado" : status === "rejected" ? "Solicitud rechazada" : "Acceso revocado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };
  const filtered = filter === "all" ? list : list.filter((r) => r.status === filter);
  const fmtDateShort = (iso) => iso ? new Date(iso).toLocaleString("es-CL", { timeZone: "America/Santiago", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  const STATUS = {
    pending:  { cls: "bg-amber-100 text-amber-800 border-amber-200", text: "Pendiente" },
    approved: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", text: "Aprobado" },
    rejected: { cls: "bg-rose-100 text-rose-800 border-rose-200", text: "Rechazado" },
    revoked:  { cls: "bg-slate-100 text-slate-700 border-slate-200", text: "Revocado" },
  };

  return (
    <div className="space-y-4" data-testid="seller-doc-access">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><FileLock2 className="w-5 h-5" /> Solicitudes de acceso a documentos</h2>
        <div className="ml-auto flex gap-1">
          {[["all", "Todas"], ["pending", "Pendientes"], ["approved", "Aprobadas"], ["rejected", "Rechazadas"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-zinc-200 hover:bg-zinc-50"}`}
              data-testid={`doc-access-filter-${k}`}
            >{l}</button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Negocio</TableHead>
            <TableHead>Inversionista</TableHead>
            <TableHead>Justificación</TableHead>
            <TableHead>Fecha</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">Sin solicitudes.</TableCell></TableRow>}
            {filtered.map((r) => {
              const s = STATUS[r.status] || STATUS.pending;
              return (
                <TableRow key={r.id} data-testid={`doc-access-row-${r.id}`}>
                  <TableCell className="font-medium">{r.business_title}</TableCell>
                  <TableCell className="text-slate-500">{r.investor_alias}</TableCell>
                  <TableCell className="text-sm max-w-xs">
                    <p className="line-clamp-2 text-slate-700">{r.justification || <span className="text-slate-400">—</span>}</p>
                    {r.status === "pending" && (
                      <Input
                        placeholder="Notas (opcional)"
                        value={notesById[r.id] || ""}
                        onChange={(e) => setNotesById({ ...notesById, [r.id]: e.target.value })}
                        className="mt-1 h-8 text-xs"
                      />
                    )}
                    {r.decision_notes && <p className="mt-1 text-xs text-slate-500 italic">Nota: {r.decision_notes}</p>}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{fmtDateShort(r.created_at)}</TableCell>
                  <TableCell><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${s.cls}`}>{s.text}</span></TableCell>
                  <TableCell className="space-x-2 whitespace-nowrap">
                    {r.status === "pending" && (
                      <>
                        <Button size="sm" className="btn-investment" onClick={() => decide(r.id, "approved")} data-testid={`approve-${r.id}`}>Aprobar</Button>
                        <Button size="sm" variant="outline" onClick={() => decide(r.id, "rejected")} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`reject-${r.id}`}>Rechazar</Button>
                      </>
                    )}
                    {r.status === "approved" && (
                      <Button size="sm" variant="outline" onClick={() => decide(r.id, "revoked")} className="text-slate-700" data-testid={`revoke-${r.id}`}>Revocar acceso</Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function MyRequests() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/requests/mine").then((r) => setList(r.data)); }, []);
  const KIND = {
    info:  { label: "Información", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    offer: { label: "Oferta",      cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    visit: { label: "Visita",      cls: "bg-amber-100 text-amber-800 border-amber-200" },
    meeting:{ label: "Reunión",    cls: "bg-violet-100 text-violet-800 border-violet-200" },
  };
  const fmtDateShort = (iso) => iso ? new Date(iso).toLocaleString("es-CL", { timeZone: "America/Santiago", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto" data-testid="seller-requests">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Negocio</TableHead>
          <TableHead>Inversionista</TableHead>
          <TableHead>Tipo</TableHead>
          <TableHead>Detalle</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead>Fecha</TableHead>
          <TableHead>Proceso</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {list.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">Aún no recibes solicitudes.</TableCell></TableRow>}
          {list.map((r) => {
            const k = KIND[r.kind || "info"];
            return (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.business_title}</TableCell>
                <TableCell className="text-slate-500">{r.investor_alias}</TableCell>
                <TableCell><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${k.cls}`}>{k.label}</span></TableCell>
                <TableCell className="text-sm">
                  {r.kind === "offer" && <span className="font-semibold text-emerald-700">{fmtCLP(r.offer_amount)}</span>}
                  {r.kind === "visit" && <span>{fmtDateShort(r.visit_date)}</span>}
                  {(!r.kind || r.kind === "info") && <span className="text-slate-400">—</span>}
                </TableCell>
                <TableCell><StatusBadge status={r.status} type="request" /></TableCell>
                <TableCell className="text-xs text-slate-500">{fmtDateShort(r.created_at)}</TableCell>
                <TableCell>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" data-testid={`view-process-${r.id}`}>Ver proceso</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Proceso · {r.business_title}</DialogTitle>
                      </DialogHeader>
                      <ProcessTimeline businessId={r.business_id} investorId={r.investor_id} />
                    </DialogContent>
                  </Dialog>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function Conversation() {
  return null; // Sellers don't participate in chat anymore
}


// ============ FORM HELPERS ============

function MoneyInput({ value, onChange, ...props }) {
  const display = value === "" || value == null
    ? ""
    : "$" + Number(String(value).replace(/\D/g, "") || "0").toLocaleString("es-CL");
  return (
    <Input
      {...props}
      type="text"
      inputMode="numeric"
      value={display}
      onChange={(e) => {
        const raw = e.target.value.replace(/\D/g, "");
        onChange(raw);
      }}
    />
  );
}

function IncludeCard({ title, desc, checked, onChange }) {
  return (
    <label className={`border rounded-lg p-4 cursor-pointer transition ${checked ? "border-slate-900 bg-slate-50" : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-sm">{title}</p>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">{desc}</p>
        </div>
        <Switch checked={checked} onCheckedChange={onChange} className="shrink-0" />
      </div>
    </label>
  );
}

const DAYS = [
  { k: "lun", short: "L", long: "Lunes" },
  { k: "mar", short: "M", long: "Martes" },
  { k: "mie", short: "X", long: "Miércoles" },
  { k: "jue", short: "J", long: "Jueves" },
  { k: "vie", short: "V", long: "Viernes" },
  { k: "sab", short: "S", long: "Sábado" },
  { k: "dom", short: "D", long: "Domingo" },
];

function defaultSchedule() {
  return DAYS.reduce((a, d) => ({ ...a, [d.k]: { open: false, all_day: false, from: "09:00", to: "18:00" } }), {});
}

function parseSchedule(raw) {
  if (!raw) return defaultSchedule();
  try {
    const obj = JSON.parse(raw);
    if (obj && typeof obj === "object" && obj.lun !== undefined) {
      // ensure shape
      return DAYS.reduce((a, d) => ({ ...a, [d.k]: obj[d.k] || { open: false, all_day: false, from: "09:00", to: "18:00" } }), {});
    }
  } catch { /* fallthrough: legacy text */ }
  return defaultSchedule();
}

function serializeSchedule(sched) {
  return JSON.stringify(sched);
}

function describeSchedule(sched) {
  const parts = DAYS.filter((d) => sched[d.k]?.open).map((d) => {
    const s = sched[d.k];
    return s.all_day ? `${d.short} 24h` : `${d.short} ${s.from}-${s.to}`;
  });
  return parts.length ? parts.join(" · ") : "Sin días seleccionados";
}

function ScheduleEditor({ value, onChange }) {
  const [sched, setSched] = useState(() => parseSchedule(value));

  const update = (k, patch) => {
    const next = { ...sched, [k]: { ...sched[k], ...patch } };
    setSched(next);
    onChange(serializeSchedule(next));
  };

  return (
    <div className="border border-zinc-200 rounded-lg p-4 bg-white space-y-3">
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-slate-600" />
        <Label className="m-0">Horario de Atención del Negocio</Label>
      </div>

      <div className="divide-y divide-zinc-100">
        {DAYS.map((d) => {
          const s = sched[d.k];
          return (
            <div key={d.k} className="py-2 flex items-center gap-3 flex-wrap" data-testid={`sched-row-${d.k}`}>
              <button
                type="button"
                onClick={() => update(d.k, { open: !s.open })}
                className={`w-9 h-9 rounded-md font-bold text-sm transition ${s.open ? "bg-slate-900 text-white" : "bg-zinc-100 text-slate-400 hover:bg-zinc-200"}`}
                title={d.long}
                data-testid={`sched-day-${d.k}`}
              >
                {d.short}
              </button>
              <span className="w-20 text-xs text-slate-500 hidden sm:inline">{d.long}</span>
              {s.open ? (
                <>
                  <label className="flex items-center gap-2 text-xs">
                    <Switch checked={s.all_day} onCheckedChange={(v) => update(d.k, { all_day: v })} />
                    <span>24 horas</span>
                  </label>
                  {!s.all_day && (
                    <div className="flex items-center gap-2">
                      <Input type="time" value={s.from} onChange={(e) => update(d.k, { from: e.target.value })} className="w-28" data-testid={`sched-from-${d.k}`} />
                      <span className="text-slate-400">a</span>
                      <Input type="time" value={s.to} onChange={(e) => update(d.k, { to: e.target.value })} className="w-28" data-testid={`sched-to-${d.k}`} />
                    </div>
                  )}
                </>
              ) : (
                <span className="text-xs text-slate-400">Cerrado</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-xs text-slate-500 pt-2 border-t border-zinc-100">
        <strong className="text-slate-700">Resumen:</strong> {describeSchedule(sched)}
      </div>
    </div>
  );
}

function ChipSelect({ label, help, options, value, onChange, testId }) {
  const selected = (value || "").split(",").map((s) => s.trim()).filter(Boolean);
  const [custom, setCustom] = useState("");

  const toggle = (opt) => {
    const next = selected.includes(opt) ? selected.filter((x) => x !== opt) : [...selected, opt];
    onChange(next.join(", "));
  };
  const addCustom = () => {
    const t = custom.trim();
    if (!t) return;
    if (!selected.includes(t)) onChange([...selected, t].join(", "));
    setCustom("");
  };
  const remove = (opt) => onChange(selected.filter((x) => x !== opt).join(", "));

  // items not in predefined list are custom
  const allOptions = [...options];
  selected.forEach((s) => { if (!allOptions.includes(s)) allOptions.push(s); });

  return (
    <div data-testid={`chips-${testId}`}>
      <Label>{label}</Label>
      {help && <p className="text-xs text-slate-500 mt-1">{help}</p>}

      <div className="mt-3 flex flex-wrap gap-2">
        {allOptions.map((opt) => {
          const isSel = selected.includes(opt);
          const isCustomOne = !options.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              data-testid={`chip-${testId}-${opt}`}
              className={`group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                isSel
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-zinc-200 hover:border-slate-400"
              }`}
            >
              {isSel && <Check className="w-3 h-3" />}
              <span>{opt}</span>
              {isSel && isCustomOne && (
                <span
                  onClick={(e) => { e.stopPropagation(); remove(opt); }}
                  className="ml-1 hover:text-rose-300"
                >
                  <X className="w-3 h-3" />
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
          placeholder="¿No está en la lista? Escríbelo y agrégalo"
          className="max-w-md"
          data-testid={`chips-input-${testId}`}
        />
        <Button type="button" size="sm" variant="outline" onClick={addCustom} data-testid={`chips-add-${testId}`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Agregar
        </Button>
      </div>
    </div>
  );
}

